import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy, limit, deleteDoc, doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { app } from '../index';
import { useTranslation } from 'react-i18next';
import './Offres.css';

let schemasCache;
const getSchemas = () => {
  if (schemasCache) return schemasCache;
  try {
    // eslint-disable-next-line global-require, import/no-extraneous-dependencies
    schemasCache = require('@nutriscore-b6448/shared-schemas');
  } catch (error) {
    console.error('Impossible de charger @nutriscore-b6448/shared-schemas:', error);
    schemasCache = {
      computeScoreAndDetails: () => ({ score: null, scoreDetail: [] })
    };
  }
  return schemasCache;
};

function Offres() {
  const { t } = useTranslation();
  const [jobOffers, setJobOffers] = useState([]);
  const [selectedJobOffer, setSelectedJobOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [associatedData, setAssociatedData] = useState(null);
  const [corrections, setCorrections] = useState({});
  const [saving, setSaving] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModelForBulkDelete, setSelectedModelForBulkDelete] = useState('');
  const [deletingAllOffers, setDeletingAllOffers] = useState(false);
  const [bulkDeleteMessage, setBulkDeleteMessage] = useState('');
  const [computedScore, setComputedScore] = useState(null);
  const [computedScoreDetail, setComputedScoreDetail] = useState([]);
  const [debugBusinessInitiativeList, setDebugBusinessInitiativeList] = useState([]);
  const [debugExperimentalEnvironmentList, setDebugExperimentalEnvironmentList] = useState([]);
  const [debugAnalysisList, setDebugAnalysisList] = useState([]);
  const [deletingCorrections, setDeletingCorrections] = useState(false);

  // Fonctions utilitaires pour convertir HTML <ul><li> en liste éditable
  const htmlToList = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') return [];
    
    // Extraire le contenu des <li> du HTML
    const liRegex = /<li[^>]*>(.*?)<\/li>/g;
    const items = [];
    let match;
    
    while ((match = liRegex.exec(htmlString)) !== null) {
      // Nettoyer le HTML des <li> pour ne garder que le texte
      const cleanText = match[1].replace(/<[^>]*>/g, '').trim();
      if (cleanText) {
        items.push(cleanText);
      }
    }
    
    return items.length > 0 ? items : [''];
  };

  const listToHtml = (items) => {
    if (!items || items.length === 0) return '';
    
    // Filtrer les éléments vides et créer le HTML
    const validItems = items.filter(item => item.trim() !== '');
    if (validItems.length === 0) return '';
    
    const liElements = validItems.map(item => `<li>${item.trim()}</li>`).join('');
    return `<ul>${liElements}</ul>`;
  };

  // Fonctions pour gérer les listes éditable (champs debug et champs d'analyse)
  const handleDebugListChange = (category, parentCategory, index, value) => {
    let setter;
    
    if (parentCategory === 'businessInitiative' && category === 'debug') {
      setter = setDebugBusinessInitiativeList;
    } else if (parentCategory === 'experimentalEnvironment' && category === 'debug') {
      setter = setDebugExperimentalEnvironmentList;
    } else if (parentCategory === 'analysis' && category === 'debug') {
      setter = setDebugAnalysisList;
    }
    
    if (setter) {
      setter(prev => {
        const newList = [...prev];
        newList[index] = value;
        return newList;
      });
    }
  };

  const addDebugListItem = (category, parentCategory) => {
    let setter;
    
    if (parentCategory === 'businessInitiative' && category === 'debug') {
      setter = setDebugBusinessInitiativeList;
    } else if (parentCategory === 'experimentalEnvironment' && category === 'debug') {
      setter = setDebugExperimentalEnvironmentList;
    } else if (parentCategory === 'analysis' && category === 'debug') {
      setter = setDebugAnalysisList;
    }
    
    if (setter) {
      setter(prev => [...prev, '']);
    }
  };

  const removeDebugListItem = (category, parentCategory, index) => {
    let setter;
    
    if (parentCategory === 'businessInitiative' && category === 'debug') {
      setter = setDebugBusinessInitiativeList;
    } else if (parentCategory === 'experimentalEnvironment' && category === 'debug') {
      setter = setDebugExperimentalEnvironmentList;
    } else if (parentCategory === 'analysis' && category === 'debug') {
      setter = setDebugAnalysisList;
    }
    
    if (setter) {
      setter(prev => prev.filter((_, i) => i !== index));
    }
  };

  const moveDebugListItem = (category, parentCategory, index, direction) => {
    let setter;
    
    if (parentCategory === 'businessInitiative' && category === 'debug') {
      setter = setDebugBusinessInitiativeList;
    } else if (parentCategory === 'experimentalEnvironment' && category === 'debug') {
      setter = setDebugExperimentalEnvironmentList;
    } else if (parentCategory === 'analysis' && category === 'debug') {
      setter = setDebugAnalysisList;
    }
    
    if (setter) {
      setter(prev => {
        const newList = [...prev];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        
        // Vérifier que le déplacement est possible
        if (newIndex >= 0 && newIndex < newList.length) {
          // Échanger les éléments
          [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
        }
        
        return newList;
      });
    }
  };

  const computeNutriScore = useCallback(() => {
    if (!associatedData) return;

    try {
      // Construire les critères Business Initiative à partir des valeurs actuelles des champs
      const businessInitiativeCriteria = {};
      
      if (associatedData.businessInitiative?.criteria) {
        Object.entries(associatedData.businessInitiative.criteria).forEach(([category, criteria]) => {
          if (typeof criteria === 'object' && criteria !== null) {
            businessInitiativeCriteria[category] = {};
            Object.entries(criteria).forEach(([key, originalValue]) => {
              const correctionKey = `businessInitiative.${category}.${key}`;
              // Utiliser la valeur actuelle du formulaire
              businessInitiativeCriteria[category][key] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : originalValue;
            });
          } else {
            // Pour les champs simples comme debug
            if (category === 'debug') {
              businessInitiativeCriteria[category] = listToHtml(debugBusinessInitiativeList);
            } else {
              const correctionKey = `businessInitiative.${category}`;
              businessInitiativeCriteria[category] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : criteria;
            }
          }
        });
      }

      // Construire les critères Experimental Environment à partir des valeurs actuelles des champs
      const experimentalEnvironmentCriteria = {};
      
      if (associatedData.experimentalEnvironment?.criteria) {
        Object.entries(associatedData.experimentalEnvironment.criteria).forEach(([category, criteria]) => {
          if (typeof criteria === 'object' && criteria !== null) {
            experimentalEnvironmentCriteria[category] = {};
            Object.entries(criteria).forEach(([key, originalValue]) => {
              const correctionKey = `experimentalEnvironment.${category}.${key}`;
              // Utiliser la valeur actuelle du formulaire
              experimentalEnvironmentCriteria[category][key] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : originalValue;
            });
          } else {
            // Pour les champs simples comme debug
            if (category === 'debug') {
              experimentalEnvironmentCriteria[category] = listToHtml(debugExperimentalEnvironmentList);
            } else {
              const correctionKey = `experimentalEnvironment.${category}`;
              experimentalEnvironmentCriteria[category] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : criteria;
            }
          }
        });
      }

      const inputData = {
        businessInitiative: businessInitiativeCriteria,
        experimentalEnvironment: experimentalEnvironmentCriteria        
      };

      // Calculer le score et le détail
      const result = getSchemas().computeScoreAndDetails(inputData);      
      setComputedScore(result.score);
      setComputedScoreDetail(result.scoreDetail || []);      
    } catch (error) {
      console.error('Erreur lors du calcul du NutriScore:', error);
      setComputedScore(null);
      setComputedScoreDetail([]);
    }
  }, [associatedData, corrections, debugBusinessInitiativeList, debugExperimentalEnvironmentList]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchJobOffers(),
          fetchAvailableModels()
        ]);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, []);

  // Recalculer le NutriScore quand les corrections changent ou au chargement initial
  useEffect(() => {
    if (associatedData) {
      computeNutriScore();
    }
  }, [corrections, associatedData, computeNutriScore]);

  const fetchJobOffers = async () => {
    const db = getFirestore(app);
    const jobOffersRef = collection(db, 'jobOffersForEvals');
    
    // Récupérer les offres d'emploi triées par date de création (plus récentes en premier)
    const q = query(jobOffersRef, orderBy('createdAt', 'desc'), limit(100));
    const querySnapshot = await getDocs(q);
    
    const offers = [];
    querySnapshot.forEach((doc) => {
      offers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    setJobOffers(offers);
    setError(null);
  };

  const fetchAvailableModels = async () => {
    const db = getFirestore(app);
    const jobOffersRef = collection(db, 'jobOffersForEvals');
    const querySnapshot = await getDocs(jobOffersRef);
    
    const models = new Set();
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.model) {
        models.add(data.model);
      }
    });
    
    setAvailableModels(Array.from(models).sort());
  };

  const handleJobOfferSelect = async (event) => {
    const selectedId = event.target.value;
    if (!selectedId) {
      setSelectedJobOffer(null);
      setAssociatedData(null);
      setCorrections({});
      return;
    }

    const selectedOffer = jobOffers.find(offer => offer.id === selectedId);
    if (selectedOffer) {
      setSelectedJobOffer(selectedOffer);
      await fetchAssociatedData(selectedOffer);
    }
  };

  const fetchAssociatedData = async (jobOffer) => {
    try {
      const db = getFirestore(app);
      const data = {};
      
      // Récupérer business initiative
      if (jobOffer.businessInitiativeId) {
        const biDoc = await getDoc(doc(db, 'businessInitiatives', jobOffer.businessInitiativeId));
        if (biDoc.exists()) {
          data.businessInitiative = biDoc.data();
        }
      }
      
      // Récupérer experimental environment
      if (jobOffer.experimentalEnvironmentId) {
        const eeDoc = await getDoc(doc(db, 'experimentalEnvironments', jobOffer.experimentalEnvironmentId));
        if (eeDoc.exists()) {
          data.experimentalEnvironment = eeDoc.data();
        }
      }
      
      if (jobOffer.explanationId) {
        const expDoc = await getDoc(doc(db, 'explanations', jobOffer.explanationId));
        if (expDoc.exists()) {
          data.explanation = expDoc.data();
        }
      }
      
      setAssociatedData(data);
      
      // Initialiser les listes debug (seulement pour les champs debug, pas debugSpecialQuestion)
      // Utiliser correctedCriteria si disponible, sinon la valeur originale
      const businessInitiativeDebug = data.businessInitiative?.correctedCriteria?.debug || data.businessInitiative?.criteria?.debug;
      if (businessInitiativeDebug) {
        setDebugBusinessInitiativeList(htmlToList(businessInitiativeDebug));
      } else {
        setDebugBusinessInitiativeList(['']);
      }
      
      const experimentalEnvironmentDebug = data.experimentalEnvironment?.correctedCriteria?.debug || data.experimentalEnvironment?.criteria?.debug;
      if (experimentalEnvironmentDebug) {
        setDebugExperimentalEnvironmentList(htmlToList(experimentalEnvironmentDebug));
      } else {
        setDebugExperimentalEnvironmentList(['']);
      }
      
      // Initialiser la liste d'analyse debug
      const analysisDebug = data.explanation?.correctedExplanation?.debug || data.explanation?.debug;
      if (analysisDebug) {
        setDebugAnalysisList(htmlToList(analysisDebug));
      } else {
        setDebugAnalysisList(['']);
      }
      
      // Pré-remplir les corrections avec les valeurs existantes
      const initialCorrections = {};
      
      // Business Initiative
      if (data.businessInitiative?.criteria) {
        Object.entries(data.businessInitiative.criteria).forEach(([category, criteria]) => {
          if (typeof criteria === 'object' && criteria !== null) {
            Object.entries(criteria).forEach(([key, value]) => {
              const correctionKey = `businessInitiative.${category}.${key}`;
              // Utiliser correctedCriteria si disponible, sinon la valeur originale
              if (data.businessInitiative.correctedCriteria?.[category]?.[key] !== undefined) {
                initialCorrections[correctionKey] = data.businessInitiative.correctedCriteria[category][key];
              } else {
                initialCorrections[correctionKey] = value;
              }
            });
          } else {
            // Utiliser correctedCriteria si disponible, sinon la valeur originale
            if (data.businessInitiative.correctedCriteria?.[category] !== undefined) {
              initialCorrections[`businessInitiative.${category}`] = data.businessInitiative.correctedCriteria[category];
            } else {
              initialCorrections[`businessInitiative.${category}`] = criteria;
            }
          }
        });
      }

      // Experimental Environment
      if (data.experimentalEnvironment?.criteria) {
        Object.entries(data.experimentalEnvironment.criteria).forEach(([category, criteria]) => {
          if (typeof criteria === 'object' && criteria !== null) {
            Object.entries(criteria).forEach(([key, value]) => {
              const correctionKey = `experimentalEnvironment.${category}.${key}`;
              // Utiliser correctedCriteria si disponible, sinon la valeur originale
              if (data.experimentalEnvironment.correctedCriteria?.[category]?.[key] !== undefined) {
                initialCorrections[correctionKey] = data.experimentalEnvironment.correctedCriteria[category][key];
              } else {
                initialCorrections[correctionKey] = value;
              }
            });
          } else {
            // Utiliser correctedCriteria si disponible, sinon la valeur originale
            if (data.experimentalEnvironment.correctedCriteria?.[category] !== undefined) {
              initialCorrections[`experimentalEnvironment.${category}`] = data.experimentalEnvironment.correctedCriteria[category];
            } else {
              initialCorrections[`experimentalEnvironment.${category}`] = criteria;
            }
          }
        });
      }      

        // Explication
        if (data.explanation) {
          // Utiliser correctedExplanation si disponible pour chaque champ
          if (data.explanation.correctedExplanation) {
            // Si il y a des corrections, les utiliser
            if (data.explanation.correctedExplanation.explanation !== undefined) {
              initialCorrections['explanation'] = data.explanation.correctedExplanation.explanation;
            } else if (data.explanation.explanation) {
              initialCorrections['explanation'] = data.explanation.explanation;
            }
            if (data.explanation.correctedExplanation.debug !== undefined) {
              initialCorrections['debug'] = data.explanation.correctedExplanation.debug;
            } else if (data.explanation.debug) {
              initialCorrections['debug'] = data.explanation.debug;
            }
            if (data.explanation.correctedExplanation.improve !== undefined) {
              initialCorrections['improve'] = data.explanation.correctedExplanation.improve;
            } else if (data.explanation.improve !== undefined) {
              initialCorrections['improve'] = data.explanation.improve;
            }
            if (data.explanation.correctedExplanation.references !== undefined) {
              initialCorrections['references'] = data.explanation.correctedExplanation.references;
            } else if (data.explanation.references) {
              initialCorrections['references'] = data.explanation.references;
            }
            if (data.explanation.correctedExplanation.company !== undefined) {
              initialCorrections['company'] = data.explanation.correctedExplanation.company;
            } else if (data.explanation.company) {
              initialCorrections['company'] = data.explanation.company;
            }
            if (data.explanation.correctedExplanation.position !== undefined) {
              initialCorrections['position'] = data.explanation.correctedExplanation.position;
            } else if (data.explanation.position) {
              initialCorrections['position'] = data.explanation.position;
            }
            // Note: score et scoreDetail sont calculés dynamiquement, pas stockés dans corrections
          } else {
            // Sinon utiliser les valeurs originales
            if (data.explanation.explanation) {
              initialCorrections['explanation'] = data.explanation.explanation;
            }
            if (data.explanation.debug) {
              initialCorrections['debug'] = data.explanation.debug;
            }
            if (data.explanation.improve !== undefined) {
              initialCorrections['improve'] = data.explanation.improve;
            }
            if (data.explanation.references) {
              initialCorrections['references'] = data.explanation.references;
            }
            if (data.explanation.company) {
              initialCorrections['company'] = data.explanation.company;
            }
            if (data.explanation.position) {
              initialCorrections['position'] = data.explanation.position;
            }
          }
        }
      
      setCorrections(initialCorrections);
      
    } catch (error) {
      console.error('Erreur lors de la récupération des données associées:', error);
    }
  };

  const handleCorrectionChange = (key, value) => {
    setCorrections(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getCriteriaLabel = (key) => {
    const labels = {
      // Business Initiative
      businessModelDefinition: t('OffersBusinessModelDefinition'),
      productVision: t('OffersProductVision'),
      productStrategyDefinition: t('OffersProductStrategyDefinition'),
      productMarketingActivities: t('OffersProductMarketingActivities'),
      growthActivities: t('OffersGrowthActivities'),
      userExperienceActivities: t('OffersUserExperienceActivities'),
      deliveryActivities: t('OffersDeliveryActivities'),
      deliveryListedBeforeTacticsAndStrategy: t('OffersDeliveryListedBeforeTacticsAndStrategy'),
      featureTeam: t('OffersFeatureTeam'),
      titleIsProductOwner: t('OffersTitleIsProductOwner'),
      productManagerWorksWithDistinctProductOwner: t('OffersProductManagerWorksWithDistinctProductOwner'),
      usesSAFe: t('OffersUsesSAFe'),
      collaboratesWithDistinctBusinessTeam: t('OffersCollaboratesWithDistinctBusinessTeam'),
      
      // Experimental Environment
      productMarketingCustomerDiscovery: t('OffersProductMarketingCustomerDiscovery'),
      growthMarketingCustomerDiscovery: t('OffersGrowthMarketingCustomerDiscovery'),
      UXCustomerDiscovery: t('OffersUXCustomerDiscovery'),
      continuousDelivery: t('OffersContinuousDelivery'),
      internalProduct: t('OffersInternalProduct'),
            
    };
    
    return labels[key] || key;
  };

  const handleDelete = async () => {
    if (!selectedJobOffer) return;
    
    const confirmMessage = `Êtes-vous sûr de vouloir supprimer cette offre d'emploi ? Cette action est irréversible et supprimera également toutes les données associées (analyses, critères, explications).`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const db = getFirestore(app);
      
      // Supprimer les documents associés
      if (selectedJobOffer.businessInitiativeId) {
        await deleteDoc(doc(db, 'businessInitiatives', selectedJobOffer.businessInitiativeId));
      }
      if (selectedJobOffer.experimentalEnvironmentId) {
        await deleteDoc(doc(db, 'experimentalEnvironments', selectedJobOffer.experimentalEnvironmentId));
      }
      if (selectedJobOffer.explanationId) {
        await deleteDoc(doc(db, 'explanations', selectedJobOffer.explanationId));
      }
      
      // Supprimer le document principal jobOffer
      await deleteDoc(doc(db, 'jobOffersForEvals', selectedJobOffer.id));
      
      // Rafraîchir la liste
      await fetchJobOffers();
      setSelectedJobOffer(null);
      setAssociatedData(null);
      setCorrections({});
      
      alert('Offre d\'emploi supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression : ' + error.message);
    }
  };

  const deleteCorrections = async () => {
    if (!selectedJobOffer || !associatedData) {
      alert('Aucune offre sélectionnée');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer toutes les corrections de cette offre d\'emploi ? Cette action est irréversible.')) {
      return;
    }

    setDeletingCorrections(true);
    try {
      const db = getFirestore(app);
      let deletedItems = [];

      // Supprimer les corrections Business Initiative
      if (associatedData.businessInitiative && selectedJobOffer.businessInitiativeId) {
        await updateDoc(doc(db, 'businessInitiatives', selectedJobOffer.businessInitiativeId), {
          correctedCriteria: deleteField()
        });
        deletedItems.push(t('OffersBusinessInitiative'));
      }

      // Supprimer les corrections Experimental Environment
      if (associatedData.experimentalEnvironment && selectedJobOffer.experimentalEnvironmentId) {
        await updateDoc(doc(db, 'experimentalEnvironments', selectedJobOffer.experimentalEnvironmentId), {
          correctedCriteria: deleteField()
        });
        deletedItems.push(t('OffersExperimentalEnvironment'));
      }

      // Supprimer les corrections Explanation
      if (associatedData.explanation && selectedJobOffer.explanationId) {
        await updateDoc(doc(db, 'explanations', selectedJobOffer.explanationId), {
          correctedExplanation: deleteField()
        });
        deletedItems.push('Explanation');
      }

      // Recharger les données pour refléter les changements
      await fetchAssociatedData(selectedJobOffer);
      
      alert(`Corrections supprimées avec succès pour : ${deletedItems.join(', ')}`);
    } catch (error) {
      console.error('Erreur lors de la suppression des corrections:', error);
      alert('Erreur lors de la suppression des corrections : ' + error.message);
    } finally {
      setDeletingCorrections(false);
    }
  };

  const saveCorrections = async () => {
    if (!selectedJobOffer || !associatedData) return;

    setSaving(true);
    try {
      const db = getFirestore(app);
      let savedItems = [];

      // Sauvegarder tous les champs Business Initiative
      if (associatedData.businessInitiative) {
        const businessInitiativeCorrections = {};

        // Sauvegarder tous les critères, modifiés ou non
        Object.entries(associatedData.businessInitiative.criteria).forEach(([category, criteria]) => {
          if (typeof criteria === 'object' && criteria !== null) {
            // Pour les objets (strategicResponsibilities, tacticalAccountabilities, smells)
            businessInitiativeCorrections[category] = {};
            Object.entries(criteria).forEach(([key, value]) => {
              const correctionKey = `businessInitiative.${category}.${key}`;
              // Utiliser la valeur corrigée si disponible, sinon la valeur originale
              businessInitiativeCorrections[category][key] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : value;
            });
          } else {
            // Pour les valeurs simples (debug, debugSpecialQuestion)
            if (category === 'debug') {
              businessInitiativeCorrections[category] = listToHtml(debugBusinessInitiativeList);
            } else {
              const correctionKey = `businessInitiative.${category}`;
              businessInitiativeCorrections[category] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : criteria;
            }
          }
        });

        // Toujours sauvegarder, même si aucune modification
        await updateDoc(doc(db, 'businessInitiatives', selectedJobOffer.businessInitiativeId), {
          correctedCriteria: businessInitiativeCorrections
        });
        savedItems.push(t('OffersBusinessInitiative'));
      }

      // Sauvegarder tous les champs Experimental Environment
      if (associatedData.experimentalEnvironment) {
        const experimentalEnvironmentCorrections = {};

        // Sauvegarder tous les critères, modifiés ou non
        Object.entries(associatedData.experimentalEnvironment.criteria).forEach(([category, criteria]) => {
          if (typeof criteria === 'object' && criteria !== null) {
            // Pour les objets
            experimentalEnvironmentCorrections[category] = {};
            Object.entries(criteria).forEach(([key, value]) => {
              const correctionKey = `experimentalEnvironment.${category}.${key}`;
              // Utiliser la valeur corrigée si disponible, sinon la valeur originale
              experimentalEnvironmentCorrections[category][key] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : value;
            });
          } else {
            // Pour les valeurs simples (debug, debugSpecialQuestion)
            if (category === 'debug') {
              experimentalEnvironmentCorrections[category] = listToHtml(debugExperimentalEnvironmentList);
            } else {
              const correctionKey = `experimentalEnvironment.${category}`;
              experimentalEnvironmentCorrections[category] = corrections[correctionKey] !== undefined ? corrections[correctionKey] : criteria;
            }
          }
        });

        // Toujours sauvegarder, même si aucune modification
        await updateDoc(doc(db, 'experimentalEnvironments', selectedJobOffer.experimentalEnvironmentId), {
          correctedCriteria: experimentalEnvironmentCorrections
        });
        savedItems.push(t('OffersExperimentalEnvironment'));
      }

      // Sauvegarder les corrections d'explication (score, scoreDetail, explanation)
      if (associatedData.explanation) {
        const correctedFields = {};
        let hasCorrections = false;
        
        // Ajouter le score calculé
        if (computedScore !== null) {
          correctedFields.score = computedScore;
          hasCorrections = true;
        }
        
        // Ajouter le détail du score calculé
        if (computedScoreDetail.length > 0) {
          correctedFields.scoreDetail = computedScoreDetail;
          hasCorrections = true;
        }
        
        // Ajouter l'explication si modifiée
        if (corrections.hasOwnProperty('explanation')) {
          correctedFields.explanation = corrections['explanation'];
          hasCorrections = true;
        }
        
        // Ajouter les références si modifiées (textarea simple)
        if (corrections.hasOwnProperty('references')) {
          correctedFields.references = corrections['references'];
          hasCorrections = true;
        }
        
        const debugHtml = listToHtml(debugAnalysisList);
        if (debugHtml) {
          correctedFields.debug = debugHtml;
          hasCorrections = true;
        }
        
        // Ajouter le champ improve si modifié (textarea simple)
        if (corrections.hasOwnProperty('improve')) {
          correctedFields.improve = corrections['improve'];
          hasCorrections = true;
        }
        
        if (corrections.hasOwnProperty('company')) {
          correctedFields.company = corrections['company'];
          hasCorrections = true;
        }
        
        if (corrections.hasOwnProperty('position')) {
          correctedFields.position = corrections['position'];
          hasCorrections = true;
        }
        
        if (hasCorrections) {
          const explanationUpdates = {
            correctedExplanation: correctedFields
          };
          await updateDoc(doc(db, 'explanations', selectedJobOffer.explanationId), explanationUpdates);
          savedItems.push('Explication');
        }
      }

      if (savedItems.length > 0) {
        alert(`Corrections sauvegardées avec succès : ${savedItems.join(', ')}`);
      } else {
        alert('Aucune correction à sauvegarder.');
      }

      // Désélectionner l'offre après sauvegarde réussie
      setSelectedJobOffer(null);
      setAssociatedData(null);
      setCorrections({});
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde de la correction : ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAllOffersByModel = async () => {
    if (!selectedModelForBulkDelete) {
      setBulkDeleteMessage('Veuillez sélectionner un modèle.');
      return;
    }

    // Compter d'abord les offres à supprimer
    const db = getFirestore(app);
    const jobOffersRef = collection(db, 'jobOffersForEvals');
    const jobOffersSnapshot = await getDocs(jobOffersRef);
    
    const offersToDelete = [];
    jobOffersSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.model === selectedModelForBulkDelete) {
        offersToDelete.push({ id: doc.id, ...data });
      }
    });

    if (offersToDelete.length === 0) {
      setBulkDeleteMessage(`${t('OffersNoOffersFound')} "${selectedModelForBulkDelete}".`);
      return;
    }

    const confirmMessage = `${t('OffersBulkDeleteConfirm')} ${offersToDelete.length} ${t('OffersBulkDeleteConfirmEnd')} "${selectedModelForBulkDelete}"${t('OffersBulkDeleteConfirmWarning')}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingAllOffers(true);
    setBulkDeleteMessage(`${t('OffersBulkDeleteProgress', { count: offersToDelete.length })}`);

    try {
      let deletedCount = 0;
      let errorCount = 0;

      for (const offer of offersToDelete) {
        try {
          // Supprimer les documents associés
          if (offer.businessInitiativeId) {
            await deleteDoc(doc(db, 'businessInitiatives', offer.businessInitiativeId));
          }
          if (offer.experimentalEnvironmentId) {
            await deleteDoc(doc(db, 'experimentalEnvironments', offer.experimentalEnvironmentId));
          }
          if (offer.explanationId) {
            await deleteDoc(doc(db, 'explanations', offer.explanationId));
          }
          
          // Supprimer le document principal jobOffer
          await deleteDoc(doc(db, 'jobOffersForEvals', offer.id));
          deletedCount++;
        } catch (error) {
          console.error(`Erreur lors de la suppression de l'offre ${offer.id}:`, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setBulkDeleteMessage(`${t('OffersBulkDeleteSuccess')} ${deletedCount} ${t('OffersBulkDeleteSuccessEnd')} "${selectedModelForBulkDelete}" ${t('OffersBulkDeleteSuccessEnd2')}`);
      } else {
        setBulkDeleteMessage(`⚠️ ${deletedCount} offres supprimées, ${errorCount} erreurs. Consultez la console pour plus de détails.`);
      }

      // Rafraîchir les listes
      await fetchJobOffers();
      await fetchAvailableModels();
      
      // Réinitialiser les sélections
      setSelectedModelForBulkDelete('');
      
    } catch (error) {
      console.error('Erreur lors de la suppression en masse:', error);
      setBulkDeleteMessage(`❌ Erreur lors de la suppression : ${error.message}`);
    } finally {
      setDeletingAllOffers(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('OffersLoading')}</div>;
  }

  if (error) {
    return <div className="error">{t('OffersError')} {error}</div>;
  }

  return (
    <div className="offres-container">
      <h2>{t('OffersTitle')}</h2>
      
      {/* Section gestion des offres */}
      <div className="offres-management-section">
        <h3>{t('OffersTitle')}</h3>
        <p className="offres-description">
          {t('OffersDescription')}
        </p>
        
        <div className="offres-content">
          <div className="listbox-section">
            <label htmlFor="jobOffersList" className="listbox-label">
{t('OffersSelectLabel')}
            </label>
            <select
              id="jobOffersList"
              value={selectedJobOffer?.id || ''}
              onChange={handleJobOfferSelect}
              className="job-offers-listbox"
            >
              <option value="">{t('OffersSelectPlaceholder')}</option>
              {jobOffers.map((offer) => (
                <option key={offer.id} value={offer.id}>
                  {(offer.company && (offer.company + " - ")) || (offer.position + " - ")}{offer.model} ({new Date(offer.createdAt?.seconds * 1000 || Date.now()).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

        {selectedJobOffer && (
          <div className="selected-offer">
            <div className="offer-header">
              <h3>{t('OffersSelectedOffer')}</h3>
              <button onClick={handleDelete} className="delete-button">
                🗑️ {t('OffersDeleteButton')}
              </button>
            </div>

            <div className="offer-info">
              <p><strong>{t('OffersCompany')}</strong> {selectedJobOffer.company}</p>
              <p><strong>{t('OffersPosition')}</strong> {selectedJobOffer.position}</p>
              <p><strong>{t('OffersCreatedAt')}</strong> {new Date(selectedJobOffer.createdAt?.seconds * 1000 || Date.now()).toLocaleDateString()}</p>
              {selectedJobOffer.model && <p><strong>{t('OffersModel')}</strong> {selectedJobOffer.model}</p>}
            </div>

            <div className="detail-section">
              <h4>{t('OffersContentPreview')}</h4>
              <div className="content-preview">
                {selectedJobOffer.content}
              </div>
            </div>

            {/* Affichage des critères d'évaluation */}
            {associatedData && (
              <div className="detail-section">
                 <h4>{t('OffersEvaluationCriteria')}</h4>
                <p className="validation-description">
                  {t('OffersValidationDescription')}
                </p>
                
                {/* Business Initiative Criteria */}
                {associatedData.businessInitiative?.criteria && (
                  <div className="criteria-section">
                     <h5>{t('OffersBusinessInitiative')}</h5>
                    <div className="criteria-list">
                      {Object.entries(associatedData.businessInitiative.criteria)
                        .sort(([a], [b]) => {
                          // Ordre : strategicResponsibilities → tacticalAccountabilities → smells → debug → debugSpecialQuestion
                          const order = {
                            'strategicResponsibilities': 1,
                            'tacticalAccountabilities': 2,
                            'smells': 3,
                            'debug': 4,
                            'debugSpecialQuestion': 5
                          };
                          const orderA = order[a] || 999;
                          const orderB = order[b] || 999;
                          return orderA - orderB;
                        })
                        .map(([category, criteria]) => (
                        <div key={category} className="criteria-category">
                          <h6>{category === 'strategicResponsibilities' ? t('OffersStrategicResponsibilities') : 
                               category === 'tacticalAccountabilities' ? t('OffersTacticalAccountabilities') : 
                               category === 'smells' ? t('OffersNegativeIndicators') : 
                               category === 'debug' ? 'Debug' :
                               category === 'debugSpecialQuestion' ? 'Debug Special Question' : category}</h6>
                          {category === 'debug' ? (
                            <div className="criteria-field editable">
                              <div className="debug-list-container">
                                {debugBusinessInitiativeList.map((item, index) => (
                                  <div key={index} className="debug-list-item">
                                    <div className="debug-move-buttons">
                                      <button
                                        type="button"
                                        onClick={() => moveDebugListItem(category, 'businessInitiative', index, 'up')}
                                        className="debug-move-btn debug-move-up"
                                        disabled={index === 0}
                                        title={t('OffersMoveUp')}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => moveDebugListItem(category, 'businessInitiative', index, 'down')}
                                        className="debug-move-btn debug-move-down"
                                        disabled={index === debugBusinessInitiativeList.length - 1}
                                        title={t('OffersMoveDown')}
                                      >
                                        ↓
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      value={item}
                                      onChange={(e) => handleDebugListChange(category, 'businessInitiative', index, e.target.value)}
                                      className="debug-list-input"
                                      placeholder={`Élément ${index + 1}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeDebugListItem(category, 'businessInitiative', index)}
                                      className="debug-remove-btn"
                                      disabled={debugBusinessInitiativeList.length <= 1}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => addDebugListItem(category, 'businessInitiative')}
                                  className="debug-add-btn"
                                >
                                  + {t('OffersAddElement')}
                                </button>
                              </div>
                            </div>
                          ) : category === 'debugSpecialQuestion' ? (
                            <div className="criteria-field editable">
                              <textarea
                                value={corrections[`businessInitiative.${category}`] !== undefined ? corrections[`businessInitiative.${category}`] : criteria}
                                onChange={(e) => handleCorrectionChange(`businessInitiative.${category}`, e.target.value)}
                                className="criteria-textarea"
                                rows={6}
                                placeholder={t('OffersDebugSpecialQuestionPlaceholder')}
                              />
                            </div>
                          ) : typeof criteria === 'object' && criteria !== null ? (
                            Object.entries(criteria).map(([key, value]) => {
                              const correctionKey = `businessInitiative.${category}.${key}`;
                              const currentValue = corrections[correctionKey] !== undefined ? corrections[correctionKey] : value;
                              return (
                                <div key={key} className="criteria-field editable">
                                  <label>{getCriteriaLabel(key)}:</label>
                                  {typeof value === 'boolean' ? (
                                    <select
                                      value={currentValue}
                                      onChange={(e) => handleCorrectionChange(correctionKey, e.target.value === 'true')}
                                      className="criteria-select"
                                    >
                                      <option value={true}>{t('OffersTrue')}</option>
                                      <option value={false}>{t('OffersFalse')}</option>
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      value={currentValue}
                                      onChange={(e) => handleCorrectionChange(correctionKey, e.target.value)}
                                      className="criteria-input"
                                    />
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="criteria-field editable">
                              <label>{getCriteriaLabel(category)}:</label>
                              <input
                                type="text"
                                value={corrections[`businessInitiative.${category}`] !== undefined ? corrections[`businessInitiative.${category}`] : criteria}
                                onChange={(e) => handleCorrectionChange(`businessInitiative.${category}`, e.target.value)}
                                className="criteria-input"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Experimental Environment Criteria */}
                {associatedData.experimentalEnvironment?.criteria && (
                  <div className="criteria-section">
                     <h5>{t('OffersExperimentalEnvironment')}</h5>
                    <div className="criteria-list">
                      {Object.entries(associatedData.experimentalEnvironment.criteria)
                        .sort(([a], [b]) => {
                          // Ordre : productMarketingCustomerDiscovery → growthMarketingCustomerDiscovery → UXCustomerDiscovery → continuousDelivery → smells (Indicateurs Négatifs) → debug → debugSpecialQuestion
                          const order = {
                            'productMarketingCustomerDiscovery': 1,
                            'growthMarketingCustomerDiscovery': 2,
                            'UXCustomerDiscovery': 3,
                            'continuousDelivery': 4,
                            'smells': 5,
                            'debug': 6,
                            'debugSpecialQuestion': 7
                          };
                          const orderA = order[a] || 999;
                          const orderB = order[b] || 999;
                          return orderA - orderB;
                        })
                        .map(([category, criteria]) => (
                        <div key={category} className="criteria-category">
                          <h6>{category === 'smells' ? t('OffersNegativeIndicators') : 
                               category === 'debug' ? 'Debug' :
                               category === 'debugSpecialQuestion' ? 'Debug Special Question' : category}</h6>
                          {category === 'debug' ? (
                            <div className="criteria-field editable">
                              <div className="debug-list-container">
                                {debugExperimentalEnvironmentList.map((item, index) => (
                                  <div key={index} className="debug-list-item">
                                    <div className="debug-move-buttons">
                                      <button
                                        type="button"
                                        onClick={() => moveDebugListItem(category, 'experimentalEnvironment', index, 'up')}
                                        className="debug-move-btn debug-move-up"
                                        disabled={index === 0}
                                        title={t('OffersMoveUp')}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => moveDebugListItem(category, 'experimentalEnvironment', index, 'down')}
                                        className="debug-move-btn debug-move-down"
                                        disabled={index === debugExperimentalEnvironmentList.length - 1}
                                        title={t('OffersMoveDown')}
                                      >
                                        ↓
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      value={item}
                                      onChange={(e) => handleDebugListChange(category, 'experimentalEnvironment', index, e.target.value)}
                                      className="debug-list-input"
                                      placeholder={`Élément ${index + 1}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeDebugListItem(category, 'experimentalEnvironment', index)}
                                      className="debug-remove-btn"
                                      disabled={debugExperimentalEnvironmentList.length <= 1}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => addDebugListItem(category, 'experimentalEnvironment')}
                                  className="debug-add-btn"
                                >
                                  + {t('OffersAddElement')}
                                </button>
                              </div>
                            </div>
                          ) : category === 'debugSpecialQuestion' ? (
                            <div className="criteria-field editable">
                              <textarea
                                value={corrections[`experimentalEnvironment.${category}`] !== undefined ? corrections[`experimentalEnvironment.${category}`] : criteria}
                                onChange={(e) => handleCorrectionChange(`experimentalEnvironment.${category}`, e.target.value)}
                                className="criteria-textarea"
                                rows={6}
                                placeholder={t('OffersDebugSpecialQuestionPlaceholder')}
                              />
                            </div>
                          ) : typeof criteria === 'object' && criteria !== null ? (
                            Object.entries(criteria).map(([key, value]) => {
                              const correctionKey = `experimentalEnvironment.${category}.${key}`;
                              const currentValue = corrections[correctionKey] !== undefined ? corrections[correctionKey] : value;
                              return (
                                <div key={key} className="criteria-field editable">
                                  <label>{getCriteriaLabel(key)}:</label>
                                  {typeof value === 'boolean' ? (
                                    <select
                                      value={currentValue}
                                      onChange={(e) => handleCorrectionChange(correctionKey, e.target.value === 'true')}
                                      className="criteria-select"
                                    >
                                      <option value={true}>{t('OffersTrue')}</option>
                                      <option value={false}>{t('OffersFalse')}</option>
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      value={currentValue}
                                      onChange={(e) => handleCorrectionChange(correctionKey, e.target.value)}
                                      className="criteria-input"
                                    />
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="criteria-field editable">
                              <label>{getCriteriaLabel(category)}:</label>
                              {typeof criteria === 'boolean' ? (
                                <select
                                  value={corrections[`experimentalEnvironment.${category}`] !== undefined ? corrections[`experimentalEnvironment.${category}`] : criteria}
                                  onChange={(e) => handleCorrectionChange(`experimentalEnvironment.${category}`, e.target.value === 'true')}
                                  className="criteria-select"
                                >
                                  <option value={true}>Vrai</option>
                                  <option value={false}>Faux</option>
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={corrections[`experimentalEnvironment.${category}`] !== undefined ? corrections[`experimentalEnvironment.${category}`] : criteria}
                                  onChange={(e) => handleCorrectionChange(`experimentalEnvironment.${category}`, e.target.value)}
                                  className="criteria-input"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Affichage du score et explication si disponibles */}
                {associatedData?.explanation && (
                  <div className="criteria-section">
                    <h5>{t('OffersAnalysisResults')}</h5>
                    {associatedData.explanation && (
                      <>
                        <div className="criteria-field editable">
                          <label htmlFor="position-correction">{t('OffersPositionLabel')}</label>
                          <input
                            id="position-correction"
                            className="criteria-input"
                            type="text"
                            value={corrections['position'] !== undefined ? corrections['position'] : (associatedData.explanation.position || '')}
                            onChange={(e) => handleCorrectionChange('position', e.target.value)}
                            placeholder={t('OffersPositionPlaceholder')}
                          />
                        </div>

                        <div className="criteria-field editable">
                          <label htmlFor="company-correction">{t('OffersCompanyLabel')}</label>
                          <input
                            id="company-correction"
                            className="criteria-input"
                            type="text"
                            value={corrections['company'] !== undefined ? corrections['company'] : (associatedData.explanation.company || '')}
                            onChange={(e) => handleCorrectionChange('company', e.target.value)}
                            placeholder={t('OffersCompanyPlaceholder')}
                          />
                        </div>

                        <div className="criteria-field">
                          <label>{t('OffersNutriScoreCalculated')}</label>
                          <div className="computed-score-display">
                            {computedScore !== null ? computedScore.toFixed(2) : (associatedData.explanation.score || t('OffersNotCalculated'))}
                          </div>
                        </div>
                        
                        <div className="criteria-field">
                          <label>{t('OffersScoreDetailCalculated')}</label>
                          <div className="computed-score-detail-display">
                            {computedScoreDetail && Object.keys(computedScoreDetail).length > 0 ? (
                              <div className="computed-detail-entry">
                                {/* Business Initiative */}
                                {computedScoreDetail.businessInitiative && (
                                  <div className="score-detail-section">
                                    <h6>{t('OffersBusinessInitiative')}</h6>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersScore')}</strong> {computedScoreDetail.businessInitiative.score ? computedScoreDetail.businessInitiative.score.toFixed(2) : t('OffersNotDefined')}/5
                                    </div>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersAppreciation')}</strong> {computedScoreDetail.businessInitiative.appreciation || t('OffersNotDefined')}
                                    </div>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersExplanation')}</strong> {computedScoreDetail.businessInitiative.explanation || t('OffersNotDefined')}
                                    </div>
                                  </div>
                                )}

                                {/* Experimental Environment */}
                                {computedScoreDetail.experimentalEnvironment && (
                                  <div className="score-detail-section">
                                    <h6>{t('OffersExperimentalEnvironment')}</h6>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersScore')}</strong> {computedScoreDetail.experimentalEnvironment.score ? computedScoreDetail.experimentalEnvironment.score.toFixed(2) : t('OffersNotDefined')}/5
                                    </div>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersAppreciation')}</strong> {computedScoreDetail.experimentalEnvironment.appreciation || t('OffersNotDefined')}
                                    </div>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersExplanation')}</strong> {computedScoreDetail.experimentalEnvironment.explanation || t('OffersNotDefined')}
                                    </div>
                                  </div>
                                )}

                                {/* Global Evaluation */}
                                {computedScoreDetail.globalEvaluation && (
                                  <div className="score-detail-section">
                                    <h6>{t('OffersGlobalEvaluation')}</h6>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersEvaluation')}</strong> {computedScoreDetail.globalEvaluation}
                                    </div>
                                  </div>
                                )}

                                {/* References */}
                                {computedScoreDetail.references && (
                                  <div className="score-detail-section">
                                    <h6>{t('OffersReferences')}</h6>
                                    <div className="score-detail-item">
                                      <strong>{t('OffersReferences')}</strong>
                                      <div className="references-content" dangerouslySetInnerHTML={{ __html: computedScoreDetail.references }} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="no-details">
                                {associatedData.explanation.scoreDetail?.length > 0 
                                  ? t('OffersDetailsNotCalculated') 
                                  : t('OffersNoDetails')
                                }
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="criteria-field editable">
                          <label htmlFor="explanation-correction">{t('OffersExplanationLabel')}</label>
                          <textarea
                            id="explanation-correction"
                            className="criteria-textarea"
                            value={corrections['explanation'] !== undefined ? corrections['explanation'] : (associatedData.explanation.explanation || '')}
                            onChange={(e) => handleCorrectionChange('explanation', e.target.value)}
                            rows={6}
                            placeholder={t('OffersExplanationPlaceholder')}
                          />
                        </div>

                        <div className="criteria-field editable">
                          <label htmlFor="references-correction">{t('OffersReferencesLabel')}</label>
                          <textarea
                            id="references-correction"
                            className="criteria-textarea"
                            value={corrections['references'] !== undefined ? corrections['references'] : (associatedData.explanation.references || '')}
                            onChange={(e) => handleCorrectionChange('references', e.target.value)}
                            rows={4}
                            placeholder={t('OffersReferencesPlaceholder')}
                          />
                        </div>

                        <div className="criteria-field editable">
                          <label>{t('OffersDebug')}</label>
                          <div className="debug-list-container">
                            {debugAnalysisList.map((item, index) => (
                              <div key={index} className="debug-list-item">
                                <div className="debug-move-buttons">
                                  <button
                                    type="button"
                                    onClick={() => moveDebugListItem('debug', 'analysis', index, 'up')}
                                    className="debug-move-btn debug-move-up"
                                    disabled={index === 0}
                                    title={t('OffersMoveUp')}
                                  >
                                    ↑
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveDebugListItem('debug', 'analysis', index, 'down')}
                                    className="debug-move-btn debug-move-down"
                                    disabled={index === debugAnalysisList.length - 1}
                                    title={t('OffersMoveDown')}
                                  >
                                    ↓
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={item}
                                  onChange={(e) => handleDebugListChange('debug', 'analysis', index, e.target.value)}
                                  className="debug-list-input"
                                  placeholder={t('OffersDebugInfoPlaceholder')}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeDebugListItem('debug', 'analysis', index)}
                                  className="debug-remove-btn"
                                  title={t('OffersRemoveElement')}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addDebugListItem('debug', 'analysis')}
                              className="debug-add-btn"
                            >
                              + {t('OffersAddDebugInfo')}
                            </button>
                          </div>
                        </div>

                        <div className="criteria-field editable">
                          <label htmlFor="improve-correction">{t('OffersImprove')}</label>
                          <textarea
                            id="improve-correction"
                            className="criteria-textarea"
                            value={corrections['improve'] !== undefined ? corrections['improve'] : (associatedData.explanation.improve || '')}
                            onChange={(e) => handleCorrectionChange('improve', e.target.value)}
                            rows={4}
                            placeholder={t('OffersImprovementSuggestions')}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Bouton de sauvegarde (seulement pour l'explication) */}
                <div className="save-section">
                  <div className="correction-buttons">
                    <button 
                      onClick={saveCorrections} 
                      disabled={saving} 
                      className="save-button"
                    >
                      {saving ? t('OffersSaving') : t('OffersSaveCorrectionsButton')}
                    </button>
                    <button 
                      onClick={deleteCorrections} 
                      disabled={deletingCorrections || saving} 
                      className="delete-corrections-button"
                    >
                      {deletingCorrections ? t('OffersDeleting') : t('OffersDeleteCorrectionsButton')}
                    </button>
                  </div>
                  {saving && <p className="saving-message">{t('OffersSaving')}</p>}
                  {deletingCorrections && <p className="saving-message">{t('OffersDeletingCorrections')}</p>}
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Section suppression en masse par modèle */}
        <div className="bulk-delete-section">
          <h3>{t('OffersBulkDeleteTitle')}</h3>
          <p className="bulk-delete-description">
            {t('OffersBulkDeleteDescription')}
          </p>
          
          <div className="bulk-delete-controls">
            <div className="bulk-delete-model-selector">
              <label htmlFor="bulkDeleteModelSelect" className="selector-label">
                {t('OffersBulkDeleteSelectLabel')}
              </label>
              <select
                id="bulkDeleteModelSelect"
                value={selectedModelForBulkDelete}
                onChange={(e) => setSelectedModelForBulkDelete(e.target.value)}
                className="model-select"
                disabled={deletingAllOffers}
              >
                <option value="">{t('OffersBulkDeleteSelectPlaceholder')}</option>
                {availableModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="bulk-delete-buttons">
              <button 
                onClick={deleteAllOffersByModel} 
                disabled={!selectedModelForBulkDelete || deletingAllOffers}
                className="bulk-delete-button"
              >
                {deletingAllOffers ? t('OffersDeleting') : t('OffersBulkDeleteButton')}
              </button>
            </div>
          </div>
          
          {availableModels.length === 0 && (
            <p className="no-models">{t('OffersNoModelsAvailable')}</p>
          )}
          
          {bulkDeleteMessage && (
            <div className={`bulk-delete-message ${bulkDeleteMessage.includes('Erreur') || bulkDeleteMessage.includes('❌') ? 'error' : 'success'}`}>
              {bulkDeleteMessage}
            </div>
          )}
        </div>
    </div>
  );
}

export default Offres;
